import { isEmpty } from 'lodash';
import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { ContentLanguage, TableDataResponse } from 'types/general';
import { isDefined } from 'utils/isDefined';
import { getSorting } from 'utils/general';
import {
  IListCompany,
  IParentCompanyWithStats,
  ICompany,
  ICompanyContact,
} from 'types/income';
import {
  CompaniesFilters,
  ParentCompaniesFilters,
  ParentCompany,
} from './companies.types';

interface GetCompanies {
  filters: Partial<CompaniesFilters>;
}

interface GetCompany {
  companyId: string;
}

interface CreateCompany {
  name: string;
  code: string;
  country: string;
  logoId: string | null;
  managerId: string | null;
  additionalInfo: string;
  billingInfo: string;
  supportedLanguages: Uppercase<ContentLanguage>[];
  activationDate: string | null;
  deactivationDate: string | null;
  colors: string[];
  settings: {
    externalRecognitionProvider: boolean;
    canGiveRecognition: boolean;
    hidePointBalanceMobile: boolean;
  };
}

interface UpdateCompany {
  companyId: string;
  data: Partial<CreateCompany>;
}

interface UpdateCompanySettings {
  companyId: string;
  settings: Partial<CreateCompany['settings']> &
    Partial<Pick<CreateCompany, 'colors' | 'billingInfo'>>;
}

interface GetCompanyContacts {
  companyId: string;
}

interface CreateCompanyContact {
  companyId: string;
  data: {
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
  };
}

interface UpdateCompanyContact {
  contactId: string;
  data: Partial<CreateCompanyContact['data']>;
}

interface DeleteCompanyContact {
  contactId: string;
}

interface GetParentCompanies {
  filters: Partial<ParentCompaniesFilters>;
}

type UpdateParentCompanySettings = Partial<
  ParentCompany['settings'] & { logoId: string | null; colors: string[] }
>;

const getSettings = ({
  colors,
  ...settings
}: Partial<CreateCompany['settings']> &
  Partial<Pick<CreateCompany, 'colors' | 'billingInfo'>>) => {
  const parsed = {
    ...settings,
    ...(!colors
      ? {}
      : colors.length
      ? {
          branding: {
            ...(colors[0] ? { primaryColor: colors[0] } : {}),
            ...(colors[1] ? { secondaryColor: colors[1] } : {}),
          },
        }
      : {
          branding: {
            primaryColor: null,
            secondaryColor: null,
          },
        }),
  };

  return isEmpty(parsed) ? undefined : parsed;
};

export const loaders = {
  getCompanies: ({ filters }: GetCompanies) => {
    return api<TableDataResponse<IListCompany>>({
      url: Endpoints.clientAdmin.companies.root,
      version: 'v2',
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        ...getSorting(filters.sort),
        search: filters.search || undefined,
        filter: {
          companyIds: filters.companies,
          status: filters.status,
        },
      },
    });
  },
  getCompany: ({ companyId }: GetCompany) => {
    return api<ICompany>({
      url: Endpoints.clientAdmin.companies.byId.root.replace(
        ':companyId',
        companyId,
      ),
    });
  },
  createCompany: ({ colors, billingInfo, ...data }: CreateCompany) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.companies.create,
      data: {
        ...data,
        settings: getSettings({ ...data.settings, billingInfo, colors }),
      },
      method: 'post',
    });
  },
  updateCompany: ({
    companyId,
    data: { colors, billingInfo, ...data },
  }: UpdateCompany) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.companies.byId.root.replace(
        ':companyId',
        companyId,
      ),
      data: {
        ...data,
        settings: getSettings({ ...data.settings, billingInfo, colors }),
      },
      method: 'patch',
    });
  },
  updateCompanySettings: ({ companyId, settings }: UpdateCompanySettings) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.companies.byId.settings.replace(
        ':companyId',
        companyId,
      ),
      data: {
        ...getSettings(settings),
      },
      method: 'patch',
    });
  },
  getCompanyContacts: ({ companyId }: GetCompanyContacts) => {
    return api<ICompanyContact[]>({
      url: Endpoints.clientAdmin.companies.byId.contacts.replace(
        ':companyId',
        companyId,
      ),
    });
  },
  createCompanyContact: ({ companyId, data }: CreateCompanyContact) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.companies.byId.contacts.replace(
        ':companyId',
        companyId,
      ),
      data,
      method: 'post',
    });
  },
  updateCompanyContact: ({ contactId, data }: UpdateCompanyContact) => {
    return api({
      url: Endpoints.clientAdmin.companies.contacts.byId.replace(
        ':contactId',
        contactId,
      ),
      data,
      method: 'patch',
    });
  },
  deleteCompanyContact: ({ contactId }: DeleteCompanyContact) => {
    return api({
      url: Endpoints.clientAdmin.companies.contacts.byId.replace(
        ':contactId',
        contactId,
      ),
      method: 'delete',
    });
  },
  getParentCompanies: ({ filters }: GetParentCompanies) => {
    return api<TableDataResponse<IParentCompanyWithStats>>({
      url: Endpoints.clientAdmin.clients.root,
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
      },
    });
  },
  updateParentCompanySettings: ({
    merchandiseContactPersonName,
    merchandiseContactPersonPhone,
    merchandiseContactPersonEmail,
    companyExpenseEnabled,
    companyExpenseEmail,
    expenseCompensationAskIban,
    logoId,
    colors,
    ...settings
  }: UpdateParentCompanySettings) => {
    return api({
      url: Endpoints.clientAdmin.clients.own,
      method: 'patch',
      data: {
        logoId,
        settings: {
          ...settings,
          merchandiseContactPersonName: isDefined(merchandiseContactPersonName)
            ? merchandiseContactPersonName || null
            : undefined,
          merchandiseContactPersonPhone: isDefined(
            merchandiseContactPersonPhone,
          )
            ? merchandiseContactPersonPhone || null
            : undefined,
          merchandiseContactPersonEmail: isDefined(
            merchandiseContactPersonEmail,
          )
            ? merchandiseContactPersonEmail || null
            : undefined,
          company_expense_enabled: isDefined(companyExpenseEnabled)
            ? companyExpenseEnabled
            : undefined,
          company_expense_email: isDefined(companyExpenseEmail)
            ? companyExpenseEmail || null
            : undefined,
          expenseCompensationAskIban: isDefined(expenseCompensationAskIban)
            ? expenseCompensationAskIban
            : undefined,
          ...(!colors
            ? {}
            : colors.length
            ? {
                branding: {
                  ...(colors[0] ? { primaryColor: colors[0] } : {}),
                  ...(colors[1] ? { secondaryColor: colors[1] } : {}),
                },
              }
            : {
                branding: {
                  primaryColor: null,
                  secondaryColor: null,
                },
              }),
        },
      },
    });
  },
};
